import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import LoadingScreen from '../components/LoadingScreen'
import { coursePath } from '../components/courses/courseUtils'
import { coursesAPI } from '../services/api'
import type { CourseReportItem } from '../types'
import { apiErrorMessage } from '../utils/apiError'

const KIND: Record<CourseReportItem['item_type'], string> = {
  review: 'Review',
  discussion: 'Discussion',
  reply: 'Reply',
}
const REASON: Record<CourseReportItem['reason'], string> = {
  spam: 'Spam',
  abuse: 'Abuse',
  misleading: 'Misleading',
  other: 'Something else',
}

/**
 * Reports (/admin/reports, admins): course reviews, discussion threads and
 * replies people reported, oldest first, one row per item. Remove takes the
 * item down; Keep leaves it. Either settles every report of it. A course's
 * creator can't remove reviews, so an abusive one is removed here.
 */
const AdminReports = () => {
  const [items, setItems] = useState<CourseReportItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    () =>
      coursesAPI
        .reports()
        .then(setItems)
        .catch((e) => setError(apiErrorMessage(e, "Couldn't load the reports."))),
    []
  )
  useEffect(() => {
    load()
  }, [load])

  const decide = async (id: number, action: 'remove' | 'keep') => {
    setError(null)
    try {
      await coursesAPI.decideReport(id, action)
      await load()
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't save that."))
    }
  }

  if (!items && !error) return <LoadingScreen />
  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="admin-reports">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="mt-1 text-sm text-gray-600">
          Course reviews and discussion posts people reported. Remove takes the post down; Keep leaves it. Creators
          can&rsquo;t remove reviews of their own courses, so abusive ones are removed here.
        </p>
      </header>
      {error && (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      )}
      {items && items.length === 0 && <p className="text-sm text-gray-600">Nothing reported.</p>}
      <ul className="space-y-3">
        {(items || []).map((item) => (
          <li key={item.id} className="rounded-lg bg-white p-4 shadow" data-testid="report-row">
            <p className="text-xs text-gray-500">
              <span className="font-medium text-gray-900">{KIND[item.item_type]}</span> in{' '}
              <Link to={coursePath(item.course_slug)} className="text-primary-700 hover:underline">
                {item.course_title}
              </Link>{' '}
              · {REASON[item.reason]} · {item.reports} {item.reports === 1 ? 'report' : 'reports'} ·{' '}
              {new Date(item.created_at).toLocaleDateString()}
            </p>
            <p className="mt-2 whitespace-pre-line text-sm text-gray-800">
              {item.item_text === null ? (
                <span className="italic text-gray-500">Already removed by its author or the instructor.</span>
              ) : (
                <>
                  {item.item_author && <span className="font-medium">@{item.item_author}: </span>}
                  {item.item_text}
                </>
              )}
            </p>
            {item.note && <p className="mt-1 text-xs text-gray-500">Reporter&rsquo;s note: {item.note}</p>}
            <div className="mt-3 flex gap-2">
              {item.item_text !== null && (
                <button
                  type="button"
                  onClick={() => decide(item.id, 'remove')}
                  className="rounded-md bg-danger-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-danger-500"
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                onClick={() => decide(item.id, 'keep')}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50"
              >
                {item.item_text === null ? 'Clear' : 'Keep'}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default AdminReports
